'use client'

import React, { createContext, useContext, useEffect, useRef } from 'react'
import { signOut, useSession } from 'next-auth/react'

interface SessionContextType {
  heartbeat: () => void
  endSession: () => void
}

const SessionContext = createContext<SessionContextType>({
  heartbeat: () => {},
  endSession: () => {}
})

export const useSessionTracking = () => useContext(SessionContext)

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const sessionTokenRef = useRef<string | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isActiveRef = useRef(true)

  // Function to create a new session
  const createSession = async () => {
    if (!session?.user?.id) return

    try {
      const response = await fetch('/api/auth/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: session.user.id,
          token: 'dummy-token'
        })
      })

      if (response.ok) {
        const data = await response.json()
        sessionTokenRef.current = data.sessionToken
      } else if (response.status === 401) {
        signOut({ callbackUrl: "/account-deleted" })
      }
    } catch (error) {
      console.error('Failed to create session:', error)
    }
  }

  // Function to send heartbeat
  const heartbeat = async () => {
    if (!sessionTokenRef.current || !isActiveRef.current) return

    try {
      const response = await fetch('/api/auth/sessions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: sessionTokenRef.current
        })
      })
      if (response.status === 401) {
        signOut({ callbackUrl: "/account-deleted" })
      }
    } catch (error) {
      console.error('Failed to send heartbeat:', error)
    }
  }

  // Function to end session
  const endSession = async () => {
    if (!sessionTokenRef.current) return

    try {
      await fetch('/api/auth/sessions', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: sessionTokenRef.current
        })
      })
      sessionTokenRef.current = null
    } catch (error) {
      console.error('Failed to end session:', error)
    }
  }

  // Track user activity
  useEffect(() => {
    if (status !== 'authenticated' || !session?.user?.id) return

    let activityTimeout: NodeJS.Timeout

    const resetActivityTimeout = () : void => {
      isActiveRef.current = true
      clearTimeout(activityTimeout)
      
      activityTimeout = setTimeout(() => {
        isActiveRef.current = false
      }, 10 * 60 * 1000) // 10 minutes
    }

    const handleActivity = () : void => {
      resetActivityTimeout()
    }

    document.addEventListener('mousedown', handleActivity)
    document.addEventListener('mousemove', handleActivity)
    document.addEventListener('keypress', handleActivity)
    document.addEventListener('scroll', handleActivity)

    resetActivityTimeout()

    return () => {
      clearTimeout(activityTimeout)
      document.removeEventListener('mousedown', handleActivity)
      document.removeEventListener('mousemove', handleActivity)
      document.removeEventListener('keypress', handleActivity)
      document.removeEventListener('scroll', handleActivity)
    }
  }, [status, session?.user?.id])

  // Session management
  useEffect(() => {
    if (status !== 'authenticated' || !session?.user?.id) {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
        heartbeatIntervalRef.current = null
      }
      if (sessionTokenRef.current) {
        endSession()
      }
      return
    }

    createSession()

    heartbeatIntervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        heartbeat()
      }
    }, 5 * 60 * 1000) // 5 minutes

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }, [status, session?.user?.id])

  // Cleanup on unmount
  useEffect(() => {
    const handleBeforeUnload = () : void => {
      if (sessionTokenRef.current) {
        navigator.sendBeacon('/api/auth/sessions', JSON.stringify({
          sessionToken: sessionTokenRef.current
        }))
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (sessionTokenRef.current) {
        endSession()
      }
    }
  }, [])

  const contextValue: SessionContextType = {
    heartbeat,
    endSession
  }

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  )
}
